import { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Globe } from 'lucide-react';
import { useLanguage, type LanguageCode } from '@/language/LanguageContext';

const LANGUAGES: { code: LanguageCode; label: string; native: string }[] = [
  { code: 'en', label: 'English', native: 'English' },
  { code: 'hi', label: 'Hindi', native: 'हिंदी' },
  { code: 'te', label: 'Telugu', native: 'తెలుగు' },
  { code: 'ta', label: 'Tamil', native: 'தமிழ்' },
  { code: 'mr', label: 'Marathi', native: 'मराठी' },
  { code: 'kn', label: 'Kannada', native: 'ಕನ್ನಡ' },
  { code: 'ml', label: 'Malayalam', native: 'മലయാളം' },
];

interface Props {
  variant?: 'compact' | 'full';
  className?: string;
}

export function LanguageSelector({ variant = 'compact', className = '' }: Props) {
  const { i18n, t } = useTranslation();
  const { setLanguage } = useLanguage();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const current = LANGUAGES.find(l => l.code === i18n.language) ?? LANGUAGES[0];

  useEffect(() => {
    function handleClick(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  function select(code: LanguageCode) {
    setLanguage(code);
    setOpen(false);
  }

  if (variant === 'full') {
    return (
      <div className={`space-y-2 ${className}`}>
        <p className="text-xs font-semibold text-white/50 uppercase tracking-widest px-1">
          {t('nav.language')}
        </p>
        <div className="grid grid-cols-1 gap-1">
          {LANGUAGES.map(lang => (
            <button
              key={lang.code}
              onClick={() => select(lang.code)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm text-left transition
                ${current.code === lang.code
                  ? 'bg-white/20 text-white font-medium'
                  : 'text-white/70 hover:bg-white/10 hover:text-white'
                }`}
            >
              <Globe size={14} className="shrink-0" />
              <span>{lang.native}</span>
              <span className="text-white/40 text-xs ml-auto">{lang.label}</span>
            </button>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div ref={ref} className={`relative ${className}`}>
      <button
        onClick={() => setOpen(o => !o)}
        title={t('language.select')}
        className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg text-xs font-medium text-gray-600 hover:bg-gray-100 transition"
      >
        <Globe size={15} />
        <span className="uppercase">{current.code}</span>
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-48 bg-chrome-page border border-gray-200 rounded-xl shadow-lg z-50 py-1 overflow-hidden">
          <p className="px-3 pt-2 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wider">
            {t('language.select')}
          </p>
          {LANGUAGES.map(lang => (
            <button
              key={lang.code}
              onClick={() => select(lang.code)}
              className={`w-full flex items-center gap-2 px-3 py-2 text-sm text-left transition
                ${current.code === lang.code
                  ? 'bg-gray-50 font-medium text-gray-900'
                  : 'text-gray-700 hover:bg-gray-50'
                }`}
            >
              <span className="w-6 text-center text-xs font-bold text-gray-400 uppercase">{lang.code}</span>
              <span>{lang.native}</span>
              {current.code === lang.code && (
                <span className="ml-auto text-teal-600">✓</span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
